'use client';

import { Loader2, Sparkles, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

export interface PodPredictiveCalibration {
	/** Fattore di scala applicato alle curve stimate, ricavato dallo storico importato. */
	scale: number;
	/** Numero di giorni di storico usati per la calibrazione. */
	sampleDays?: number;
	/** Data/ora dell'ultima calibrazione (ISO). */
	calibratedAt?: string;
}

export interface PodPredictivePanelPod {
	/** Tipo di POD: prelievo o immissione. */
	tipo: 'withdrawal' | 'injection';
	/** Se true, la generazione di dati predittivi è attiva per questo POD. */
	predictiveEnabled?: boolean | null;
	/** Calibrazione corrente delle curve, se disponibile. */
	predictiveCalibration?: PodPredictiveCalibration | null;
}

export interface PodPredictiveImportResult {
	/** Numero di letture scritte con successo. */
	written: number;
	/** Numero di righe scartate perché non valide. */
	invalid: number;
	/** Calibrazione risultante dall'import, se calcolata. */
	calibration: PodPredictiveCalibration | null;
}

export interface PodPredictivePanelProps {
	/** POD per cui gestire i dati predittivi. */
	pod: PodPredictivePanelPod;
	/** Attiva/disattiva la generazione di dati predittivi; ritorna lo stato effettivo salvato. */
	onTogglePredictive: (enabled: boolean) => Promise<{ predictiveEnabled?: boolean | null }>;
	/** Importa uno storico di letture (già parsate) per calibrare le curve. */
	onImportCsv: (
		rows: Array<{ timestamp: string; value: number }>,
		unit: 'kwh' | 'kw',
	) => Promise<PodPredictiveImportResult>;
}

/**
 * Dati predittivi per POD senza misuratore connesso: toggle di attivazione
 * (genera letture sintetiche a 15 min via cron) e import dello storico CSV
 * usato per calibrare le curve.
 *
 * CSV atteso: due colonne `timestamp,kwh` oppure `timestamp,power_kw`
 * (formato feed/simulator) — l'header determina l'unità.
 */
export function PodPredictivePanel({
	pod,
	onTogglePredictive,
	onImportCsv,
}: PodPredictivePanelProps) {
	const [toggling, setToggling] = useState(false);
	const [importing, setImporting] = useState(false);
	const fileRef = useRef<HTMLInputElement>(null);
	const enabled = pod.predictiveEnabled ?? false;
	const calibration = pod.predictiveCalibration ?? null;

	async function handleToggle() {
		setToggling(true);
		try {
			const updated = await onTogglePredictive(!enabled);
			toast.success(
				updated.predictiveEnabled
					? 'Dati predittivi attivati - la generazione parte al prossimo ciclo (15 min)'
					: 'Dati predittivi disattivati',
			);
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Errore di aggiornamento');
		} finally {
			setToggling(false);
		}
	}

	async function handleImportFile(file: File) {
		setImporting(true);
		try {
			const text = await file.text();
			const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
			if (lines.length === 0) throw new Error('File vuoto');

			let unit: 'kwh' | 'kw' = 'kwh';
			let startIdx = 0;
			const header = lines[0].toLowerCase();
			if (header.includes('timestamp') || header.includes('data')) {
				if (header.includes('kw') && !header.includes('kwh')) unit = 'kw';
				startIdx = 1;
			}

			const rows: Array<{ timestamp: string; value: number }> = [];
			for (let i = startIdx; i < lines.length; i++) {
				const [ts, val] = lines[i].split(/[,;]/);
				if (!ts || val === undefined) continue;
				rows.push({ timestamp: ts.trim(), value: Number(val.trim()) });
			}
			if (rows.length === 0) throw new Error('Nessuna riga valida nel CSV');

			const { written, invalid, calibration: calib } = await onImportCsv(rows, unit);

			toast.success(
				`Storico importato: ${written} letture${invalid ? ` (${invalid} righe scartate)` : ''}` +
					(calib ? ` - curve calibrate (scala ${calib.scale})` : ''),
			);
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Errore nell'import dello storico");
		} finally {
			setImporting(false);
			if (fileRef.current) fileRef.current.value = '';
		}
	}

	return (
		<div className="space-y-2 rounded-lg border border-border/70 bg-background/50 p-3">
			<div className="flex items-center gap-2">
				<Sparkles className="h-3 w-3 text-accent-purple" />
				<span className="text-[11px] font-bold uppercase tracking-widest text-accent-purple">
					Dati predittivi
				</span>
				{enabled && (
					<span className="ml-auto rounded-full border border-accent-green/30 bg-accent-green/10 px-2 py-0.5 text-[11px] text-accent-green">
						Attivi
					</span>
				)}
			</div>

			<p className="text-[11px] text-text-muted">
				Per POD senza misuratore connesso: genera letture stimate ogni 15 minuti dalla curva del
				profilo di consumo del socio{pod.tipo === 'injection' && ', resa FV (PVGIS) e meteo'}.
			</p>

			<label className="flex cursor-pointer items-center gap-2 text-[13px] text-text-primary">
				<input
					type="checkbox"
					checked={enabled}
					onChange={handleToggle}
					disabled={toggling}
					className="h-3.5 w-3.5 accent-accent"
				/>
				Genera dati predittivi
				{toggling && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
			</label>

			{calibration && (
				<p className="text-[11px] text-text-muted">
					Curve calibrate su storico: scala {calibration.scale}
					{calibration.sampleDays ? ` · ${calibration.sampleDays} giorni` : ''}
					{calibration.calibratedAt
						? ` · ${new Date(calibration.calibratedAt).toLocaleDateString('it-IT')}`
						: ''}
				</p>
			)}

			<div className="flex items-center gap-2 pt-1">
				<input
					ref={fileRef}
					type="file"
					accept=".csv,text/csv"
					className="hidden"
					onChange={(e) => {
						const f = e.target.files?.[0];
						if (f) handleImportFile(f);
					}}
				/>
				<button
					type="button"
					onClick={() => fileRef.current?.click()}
					disabled={importing}
					className="inline-flex items-center gap-1 rounded border border-border px-3 py-1 text-[12px] text-text-muted transition-colors hover:border-accent hover:text-accent disabled:opacity-50"
				>
					{importing ? (
						<Loader2 className="h-3 w-3 animate-spin" />
					) : (
						<Upload className="h-3 w-3" />
					)}
					Importa storico CSV
				</button>
				<span className="text-[10px] italic text-text-muted">
					timestamp,kwh (o power_kw) - calibra le curve
				</span>
			</div>
		</div>
	);
}
